'use client';
import styled from '@emotion/styled';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { sanityClient } from '@/content/sanity/sanity-client/sanity-client';
import { nextImageTransitionProps } from '@/constants/next-image-transition-props';
import { config } from '@/config/environment';
import { urlFor } from '@/utils/buildImageSource';

const MainDiv = styled.div`
  width: 100%;
  height: auto;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #231727;
  position: relative;
  @media (max-width: 768px) {
    padding-top: 98px;
    margin-bottom: 60px;
  }
`;
const Div = styled(Grid)`
  width: 100%;
  height: auto;
  min-height: 572px;
  display: flex;
  flex-direction: row;
  padding: 0 12.5%;
  justify-content: space-between;
  @media (max-width: 768px) {
    padding: 20px;
    min-height: unset;
  }
`;
const TitleDiv = styled(Grid)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 18.33% 0 15% 0;
  height: auto;
  z-index: 1;
  @media (max-width: 768px) {
    padding: 0;
  }
`;
const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 50px;
  line-height: 109.3%;
  color: #fdf3de;
  text-align: center;
  margin: 0;
  @media (max-width: 768px) {
    text-align: start;
    margin-bottom: 32px;
  }
`;
const Text = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  line-height: 159.3%;
  color: #ffffff;
  text-align: start;
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 32px;
  }
`;
const Vector = styled(Image)`
  object-fit: cover;
  position: absolute;
  width: 43.47%;
  height: auto;
  margin-top: 60px;
  left: 0;
  @media (max-width: 768px) {
    display: none;
  }
`;
Vector.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const VideoDiv = styled.div`
  margin-top: 50px;
  height: auto;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  @media (max-width: 768px) {
    padding: 50px 20px 0 20px;
    margin-bottom: 100px;
  }
`;
const Video = styled.div`
  height: 412px;
  width: 550px;
  z-index: 2;
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
  }
`;
const Pattern = styled.img`
  display: block;
  object-fit: cover;
  position: absolute;
  height: 400px;
  width: 400px;
  margin: 100px 0 0 330px;
  z-index: 1;
`;
const MobileBlob = styled.img`
  object-fit: cover;
  height: 450px;
  width: 350px;
  right: 0;
  margin: -30px 0 0 auto;
  position: absolute;
`;
const Values = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: auto;
  padding-top: 100px;
  @media (max-width: 768px) {
    padding: 0 20px 20px 20px;
    background: #ffffff;
    padding-top: 0px;
  }
`;
const Value = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  :nth-of-type(1) {
    @media (max-width: 768px) {
      margin-top: 60px;
    }
  }
`;
const ValueText = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  line-height: 159.3%;
  text-align: center;
  color: #4c3b51;
  transition: color 0.5s linear;
  @media (max-width: 768px) {
    color: #000000;
    font-weight: 500;
    font-size: 20px;
    line-height: 137.8%;
  }
`;
const Stars = styled.img`
  height: 90px;
  width: 17px;
  margin: 20px;
`;
const Logo = styled.img`
  height: 47px;
  width: 50px;
  margin: 30px;
`;
const Wave = styled.div`
  height: 67px;
  margin-top: 60px;
`;

// Constants extracted to module scope so hooks can safely depend on stable values
const textIds = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'];
const defaultColorMap = {
  mobile: '#BDBDBD',
  desktop: '#4C3B51',
} as const;
const scrollColorMap = {
  mobile: '#000000',
  desktop: '#ffffff',
} as const;
const scrollRangeMap = {
  mobile: [150, 350] as [number, number],
  desktop: [250, 385] as [number, number],
} as const;

const Philosophy = () => {
  const [philosophyData, setPhilosophyData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const isMobile = useMediaQuery('(max-width:960px)');
  const archiveMode = config.platformMode === 'archive';

  useEffect(() => {
    const defaultColor = isMobile ? defaultColorMap.mobile : defaultColorMap.desktop;
    const scrollColor = isMobile ? scrollColorMap.mobile : scrollColorMap.desktop;
    const scrollRange = isMobile ? scrollRangeMap.mobile : scrollRangeMap.desktop;
    const getDistance = () => {
      textIds.forEach(id => {
        const element = document.getElementById(id);
        if (!element) return;
        const pos = element.getBoundingClientRect();
        if (
          pos.top >= 0 &&
          pos.bottom <= window.innerHeight - scrollRange[0] &&
          pos.bottom >= window.innerHeight - scrollRange[1]
        ) {
          element.style.color = scrollColor;
        } else {
          element.style.color = defaultColor;
        }
      });
    };
    getDistance();
    window.addEventListener('scroll', getDistance);
    return () => window.removeEventListener('scroll', getDistance);
  }, [isMobile]);

  useEffect(() => {
    const fetchPhilosophyData = async () => {
      const data = await sanityClient.fetch(
        `*[_type=="philosophy"][0]{ title, description, videoImage, videoBlob, mobileBlob, values[]{ text, icon,logo } }`,
      );
      setPhilosophyData(data);
      setLoading(false);
    };

    fetchPhilosophyData();
  }, []);

  if (loading || !philosophyData) {
    return <MainDiv></MainDiv>;
  }

  const visibleTitle = archiveMode ? "Curiko's values" : philosophyData.title;
  const visibleDescription = archiveMode
    ? 'Curiko was guided by a set of values that shaped how we designed interactions and made decisions. We continually tried to stitch these values into every experience, relationship, and experiment along the way.'
    : philosophyData.description;

  return (
    <MainDiv>
      <Div container>
        <Vector src={urlFor(philosophyData.videoBlob)} alt={'philosophy'} />
        <TitleDiv item md={6} xs={12}>
          <Title>{visibleTitle}</Title>
        </TitleDiv>
        <TitleDiv item md={6} xs={12}>
          <Text>{visibleDescription}</Text>
        </TitleDiv>
      </Div>
      <VideoDiv>
        <Video>
          <Image {...nextImageTransitionProps} src={urlFor(philosophyData.videoImage)} alt="" />
        </Video>
        {isMobile ? (
          <MobileBlob src={urlFor(philosophyData.mobileBlob)} alt={'vector'} />
        ) : (
          <Pattern
            src={urlFor(philosophyData.patternBlob ?? '/about-us/philosophy/pattern2.png')}
            alt={'vector'}
          />
        )}
      </VideoDiv>
      {isMobile && (
        <Wave>
          <svg
            width="100%"
            height="67"
            viewBox="0 0 375 67"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="375" height="1329" transform="translate(0 -852)" fill="#231727" />
            <path
              d="M756.213 2950.49V80.0584C727.928 49.4441 782.968 72.2845 699.225 46.7827C529.55 -4.88777 408.246 120.597 207.208 38.3617C111.76 -0.681301 48.2937 -13.7617 -138.806 38.3608L-149 2950.49H756.213Z"
              fill="white"
            />
            <path
              d="M272.572 -259.444C306.264 -241.602 351.061 -268.886 384.996 -265.566C418.932 -262.246 441.747 -228.451 444.68 -192.244C447.33 -156.186 430.115 -117.698 399.384 -105.256C368.423 -92.7359 324.306 -105.861 292.643 -110.145C261.217 -114.498 242.472 -110.083 205.508 -100.377C168.377 -90.3691 112.952 -75.3056 102.558 -90.9652C91.8697 -106.783 125.912 -153.48 133.733 -195.809C141.623 -237.9 123.071 -275.569 128.778 -309.504C134.484 -343.439 164.179 -373.883 190.274 -357.339C216.369 -340.795 238.809 -277.52 272.572 -259.444Z"
              fill="#81C1BB"
            />
            <defs>
              <clipPath id="clip0">
                <rect width="375" height="1329" fill="white" transform="translate(0 -852)" />
              </clipPath>
            </defs>
          </svg>
        </Wave>
      )}
      <Values>
        {philosophyData.values.map((value: any, index: number) => (
          <Value key={index}>
            <ValueText id={textIds[index]}>{value.text}</ValueText>
            {value.logo ? (
              <Logo src={urlFor(value.icon)} alt="curiko" />
            ) : (
              <Stars src={urlFor(value.icon)} alt="star" />
            )}
          </Value>
        ))}
      </Values>
    </MainDiv>
  );
};

export default Philosophy;
